import { LoginAuthorization, LoginAuthorizationModel, RefreshSSOTokenResponseModel } from '../models/login-authorization';
import { RegisteredUser } from '../models/registered-user';
import { HttpClient } from './../shared/utils/http-client';
import Bugsnag from '@bugsnag/js';

const CAPTCHA_CLIENT_TYPE = {
    ADMIN_TOOL: 'ad',
    PORTAL2: 'p2',
    PORTAL3: 'p3'
};

export interface LoginResponse {
    user_id: string,
    auth_token: string,
    success: boolean
}
export const CC_APP_TYPE = 7;

export default class LoginProvider {
    private endpointCore: string;
    private endpointCoreV1: string;

    constructor() {
        this.endpointCore = `${process.env.REACT_APP_BASE_API_URL}`;
        this.endpointCoreV1 = `${process.env.REACT_APP_BASE_API_V1_URL}`;
    };

    checkEmail = async (email: string): Promise<LoginAuthorization> => {
        const params = {
            email, 
            cctype: CC_APP_TYPE
        };
        const response = await HttpClient.post(`${this.endpointCore}login/check_login`, params);
        const { results: model }: { results: LoginAuthorizationModel } = response.data;

        return LoginAuthorization.create(model);
    }

    login = async (username: string, password: string, token?: string) => {

        if (token) {
            const captchaResponse = await HttpClient.post(
                `${this.endpointCore}recaptcha_verify`,
                {
                    token,
                    client: CAPTCHA_CLIENT_TYPE.ADMIN_TOOL,
                    version: 2
                }
            );
           
            const {
                data: { success, error_code: errorCode },
                status
            } : { 
                data: {
                    success?: boolean,
                    error_code: string
                },
                status: number
            } = captchaResponse;

            if (!success || !!errorCode || status !== 200) {
                return Promise.reject({
                    response: {
                        data: {},
                        status
                    },
                    message: errorCode
                });
            }
        }

        const params = {
            username, 
            password
        }

        return HttpClient.post(`${this.endpointCore}login/`, params).then(response => {
            const statusCode = response.status;
            if (statusCode === 200) {
                const { user_id, auth_token } = response.data;
                return Promise.resolve({
                    user_id,
                    auth_token,
                    success: true
                });
            }
            
            if (statusCode === 401) {
                return Promise.reject({
                    user_id: '',
                    auth_token: '',
                    success: false,
                    status: 401,
                    message: 'Unathorized response'
                })
            }

            return Promise.reject({
                status: statusCode,
                message: statusCode === 504 ? 'Request timeout' : 'Network error'
            });
        }).catch(error => {
            const { response } = error;
            return Promise.reject({
                status: response.status,
                message: response.status === 504
                ? 'Request timeout'
                : response['data']['error']
                ? response.data.error
                : 'Network error'
            })
        });
    }

    async refreshSSOToken(validationId: string): Promise<RefreshSSOTokenResponseModel> {
        const params = {
            validation_id: validationId
        };
        const response = await HttpClient
            .get(`${this.endpointCore}login/sso_refresh`, params);

        if (response.status !== 200) {
            Bugsnag.notify((new Error('refreshSSOToken - SSO refresh has an error')), (event) => {
                event.severity = 'error';
                event.context = 'SSO';
                event.addMetadata('sso_refresh', {
                    validation_id: validationId,
                    error: response
                });
            });

            return Promise.reject(response);
        }

        return response.data.results as RefreshSSOTokenResponseModel;
    }

    // sso_refresh Does store the linked account id of the new token in Redis
    // This step is key due to the super user and admin user special realms where
    // we need to verify the user is under the required account, calling this endpoint
    // Validates the newly created token and sets the expected redis value for it to work.
    async validateAccessToken(accessToken: string, accountId?: string, idToken?: string): Promise<void> {
        const params = {
            access_token: accessToken,
            account_id: accountId,
            cctype: CC_APP_TYPE,
            id_token: idToken
        };
        const response = await HttpClient.post(`${this.endpointCoreV1}v5/userid`, params);

        if (response.status !== 200) {
            Bugsnag.notify((new Error('validateAccessToken - Validate access token has an error')), (event) => {
                event.severity = 'error';
                event.context = 'SSO';
                event.addMetadata('sso_validate_access_token', {
                    account_id: accountId,
                    error: response
                });
            });

            return Promise.reject(response);
        }
    }

    async checkSSOAuthorization(authorization: LoginAuthorization, code: string, state = ''): Promise<RegisteredUser> {
        if (!authorization.valid) {
            return Promise.reject({errorMessage: 'Invalid Login Authorization'});
        }

        if ((authorization.validationId !== state)) {
            return Promise.reject({errorMessage: 'ValidationId does not match the state parameter'});
        }

        const params = {
            code,
            state
        };
        const response = await HttpClient
            .get(`${this.endpointCore}login/sso_validate`, params);

        if (response.status !== 200) {
            Bugsnag.notify((new Error('checkSSOAuthorization - Check SSO has an error')), (event) => {
                event.severity = 'error';
                event.context = 'SSO';
                event.addMetadata('sso_check', {
                    authorization: authorization,
                    code: code,
                    error: response
                });
            });

            return Promise.reject(false);
        }

        return RegisteredUser.createSSOUser(response.data.results);
    }

    async ssoLogout(validationId: string): Promise<string> {
        const params = {
            validation_id: validationId
        };
        const response = await HttpClient
            .get(`${this.endpointCore}login/sso_logout`, params);

        if (response.status !== 200) {
            Bugsnag.notify((new Error('ssoLogout - SSO logout has an error')), (event) => {
                event.severity = 'error';
                event.context = 'SSO';
                event.addMetadata('sso_logout', {
                    validation_id: validationId,
                    error: response
                });
            });

            return Promise.reject(false);
        }
        const {
            results:{
                logout_url: logoutUrl
            }
        } = response.data;

        return logoutUrl;
    }

}
